import { Box } from "@mui/material";
import { VList, type VListHandle } from "virtua";

export default function VirtualizedList({
  id,
  shift = false,
  itemSize = 40,
  reverse = false,
  childComponent,
  data,
  emptyState,
  refValue,
  onScroll,
  onScrollEnd,
  className,
}: {
  id: string;
  shift?: boolean;
  itemSize?: number;
  reverse?: boolean;
  childComponent: React.FC<any>;
  data: any[];
  emptyState?: React.JSX.Element;
  refValue?: React.MutableRefObject<VListHandle | undefined>;
  onScroll?: ({ startIndex, endIndex }: { startIndex: number; endIndex: number }) => void;
  onScrollEnd?: () => void;
  className?: string;
}) {
  const handleScroll = () => {
    if (refValue?.current) {
      const { scrollOffset, viewportSize, scrollSize } = refValue.current;

      // Binary search or iterate to find start index
      let startIndex = 0;
      let endIndex = data.length - 1;

      // Find start index
      for (let i = 0; i < data.length; i++) {
        const itemOffset = refValue.current.getItemOffset(i);
        if (itemOffset >= scrollOffset) {
          startIndex = i;
          break;
        }
      }

      // Find end index
      for (let i = startIndex; i < data.length; i++) {
        const itemOffset = refValue.current.getItemOffset(i);
        if (itemOffset > scrollOffset + viewportSize) {
          endIndex = i - 1;
          break;
        }
      }

      onScroll?.({ startIndex, endIndex });
    }
  };

  const renderItem = (itemData: any, index: number) => {
    if (!itemData) {
      return <></>;
    }
    const key = `${id}-itemId-${itemData?.id ?? ""}-index-${index}`;
    return <div key={key}>{childComponent(itemData, index)}</div>;
  };

  if (!emptyState) {
    emptyState = (
      <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>No items yet</Box>
    );
  }

  if (data?.length === 0 && emptyState) {
    return emptyState;
  }

  if (data.length < 5 && reverse) {
    return (
      <div
        style={{
          display: "block",
          overflowY: "auto",
          contain: "strict",
          width: "100%",
          height: "100%",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            minHeight: "100%",
            overflow: "clip",
          }}
        >
          {data?.map((item, index) => renderItem(item, index))}
        </div>
      </div>
    );
  }

  return (
    <VList
      ref={refValue}
      id={id}
      className={className}
      shift={shift}
      itemSize={itemSize}
      // TODO: Fix this type error
      // @ts-ignore
      data={data}
      onScroll={handleScroll}
      onScrollEnd={onScrollEnd}
    >
      {data?.map((item, index) => renderItem(item, index))}
    </VList>
  );
}
